import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, BarChart,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { getRequest, patchRequest } from '../../services/api/apiService';

// ── Page Statistiques — KPIs media buying servis depuis Postgres ──
// (ad_insights_daily via /api/statistics/summary — délivraison réelle de
// TOUTES les campagnes, comme Ads Manager). Ordre voulu : camemberts d'abord,
// puis les KPIs (CTR, CPA, vues…), funnel, courbes. Mobile-first : tout
// s'empile en 1 colonne, KPIs en 2 colonnes.
//
// MODE DÉMO : tant que le compte n'a AUCUNE diffusion réelle, la page affiche
// un jeu de données de démonstration (badge visible) pour que l'advertiser
// visualise le dashboard complet — remplacé automatiquement par les vraies
// données dès la première diffusion.

const KpiCard = ({ label, value, sub, color, icon }) => (
  <div className={`rounded-2xl p-3 md:p-5 ${color} flex flex-col gap-0.5 md:gap-1.5 min-w-0`}>
    <div className="flex items-center justify-between gap-1">
      <p className="text-[9px] md:text-xs font-semibold uppercase tracking-wide opacity-70 truncate">{label}</p>
      {icon && <span className="text-sm md:text-lg opacity-40 flex-shrink-0">{icon}</span>}
    </div>
    <p className="text-base md:text-2xl font-black leading-tight break-words">{value}</p>
    {sub && <p className="text-[9px] md:text-xs opacity-60 mt-0.5 truncate">{sub}</p>}
  </div>
);

const Bar2 = ({ value, max, color }) => (
  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
    <div
      className={`h-full rounded-full transition-all duration-700 ${color}`}
      style={{ width: max > 0 ? `${Math.min((value / max) * 100, 100)}%` : '0%' }}
    />
  </div>
);

const PLATFORM_META = {
  meta:     { label: 'Meta',     color: 'bg-blue-500',   hex: '#3b82f6', badge: 'bg-blue-100 text-blue-700'   },
  tiktok:   { label: 'TikTok',   color: 'bg-pink-500',   hex: '#ec4899', badge: 'bg-pink-100 text-pink-700'   },
  google:   { label: 'Google',   color: 'bg-yellow-500', hex: '#eab308', badge: 'bg-yellow-100 text-yellow-700'},
  snapchat: { label: 'Snapchat', color: 'bg-yellow-400', hex: '#facc15', badge: 'bg-yellow-50 text-yellow-600' },
  linkedin: { label: 'LinkedIn', color: 'bg-sky-600',    hex: '#0284c7', badge: 'bg-sky-100 text-sky-700'     },
};

const BRAND = '#221C51';

const fmt = {
  currency: (v, currency) => {
    const n = Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${n} ${currency}` : `€${n}`;
  },
  number: (v) => Number(v || 0).toLocaleString('fr-FR'),
  pct:    (v) => `${Number(v || 0).toFixed(2)}%`,
  compact: (v) => {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return `${Math.round(n * 100) / 100}`;
  },
  shortDate: (d) => new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
  isoDaysAgo: (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0];
  },
};

// Doit rester synchronisé avec REPORTING_CURRENCIES dans authService.js.
const CURRENCY_OPTIONS = ['USD', 'EUR', 'MAD', 'GBP', 'AED', 'SAR', 'DZD', 'TND', 'CAD'];

const todayIso = () => new Date().toISOString().split('T')[0];
const PERIOD_PRESETS = [
  { key: 'today',     label: "Aujourd'hui", range: () => ({ start: todayIso(), end: todayIso() }) },
  { key: 'yesterday', label: 'Hier',        range: () => ({ start: fmt.isoDaysAgo(1), end: fmt.isoDaysAgo(1) }) },
  { key: '7d',        label: '7 jours',     range: () => ({ start: fmt.isoDaysAgo(6), end: todayIso() }) },
  { key: '14d',       label: '14 jours',    range: () => ({ start: fmt.isoDaysAgo(13), end: todayIso() }) },
  { key: '30d',       label: '30 jours',    range: () => ({ start: fmt.isoDaysAgo(29), end: todayIso() }) },
  { key: 'month',     label: 'Ce mois',     range: () => {
    const d = new Date();
    return { start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`, end: todayIso() };
  } },
  { key: 'lastmonth', label: 'Mois dernier', range: () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const last = new Date(d.getFullYear(), d.getMonth(), 0);
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    return { start: iso(first), end: iso(last) };
  } },
  { key: '90d',       label: '90 jours',    range: () => ({ start: fmt.isoDaysAgo(89), end: todayIso() }) },
];

// ── Jeu de données de DÉMONSTRATION ───────────────────────────────
// Généré côté client (pseudo-aléatoire déterministe pour rester stable au
// re-render) — jamais envoyé au serveur, remplacé par les vraies données dès
// qu'une diffusion existe.
const KPI_FORMULAS = (t) => ({
  ctr: t.impressions > 0 ? +((t.clicks / t.impressions) * 100).toFixed(2) : 0,
  cpc: t.clicks > 0 ? +(t.spend / t.clicks).toFixed(2) : 0,
  cpm: t.impressions > 0 ? +((t.spend / t.impressions) * 1000).toFixed(2) : 0,
  cpa: t.conversions > 0 ? +(t.spend / t.conversions).toFixed(2) : 0,
  roas: t.spend > 0 ? +(t.revenue / t.spend).toFixed(2) : 0,
  conversionRate: t.clicks > 0 ? +((t.conversions / t.clicks) * 100).toFixed(2) : 0,
  cpv: t.videoViews > 0 ? +(t.spend / t.videoViews).toFixed(3) : 0,
  viewRate: t.impressions > 0 ? +((t.videoViews / t.impressions) * 100).toFixed(2) : 0,
  engagementRate: t.impressions > 0 ? +((t.engagements / t.impressions) * 100).toFixed(2) : 0,
  costPerEngagement: t.engagements > 0 ? +(t.spend / t.engagements).toFixed(3) : 0,
  aov: t.conversions > 0 && t.revenue > 0 ? +(t.revenue / t.conversions).toFixed(2) : 0,
});

const buildDemoSummary = (start, end, currency) => {
  const profiles = [
    { platform: 'meta',   share: 0.45, ctr: 2.1, convRate: 3.4, roas: 3.1, viewShare: 0.32 },
    { platform: 'tiktok', share: 0.33, ctr: 1.7, convRate: 2.2, roas: 2.3, viewShare: 0.55 },
    { platform: 'google', share: 0.22, ctr: 3.6, convRate: 4.2, roas: 3.9, viewShare: 0.12 },
  ];
  let seed = 7;
  const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };

  const days = [];
  const pTotals = {};
  const d0 = new Date(`${start}T00:00:00`);
  const d1 = new Date(`${end}T00:00:00`);
  for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const weekend = [0, 6].includes(d.getDay()) ? 0.72 : 1;
    const day = { date: key, spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0, videoViews: 0, engagements: 0 };
    for (const p of profiles) {
      const spend = (95 + rand() * 85) * p.share * weekend;
      const impressions = Math.round((spend / (5.5 + rand() * 4)) * 1000);
      const clicks = Math.round(impressions * (p.ctr / 100) * (0.85 + rand() * 0.3));
      const conversions = +(clicks * (p.convRate / 100) * (0.8 + rand() * 0.4)).toFixed(1);
      const revenue = spend * p.roas * (0.8 + rand() * 0.4);
      const videoViews = Math.round(impressions * p.viewShare * (0.85 + rand() * 0.3));
      const engagements = Math.round(impressions * (0.014 + rand() * 0.012));
      day.spend += spend; day.impressions += impressions; day.clicks += clicks;
      day.conversions += conversions; day.revenue += revenue;
      day.videoViews += videoViews; day.engagements += engagements;
      if (!pTotals[p.platform]) pTotals[p.platform] = { platform: p.platform, spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0, videoViews: 0, engagements: 0 };
      const t = pTotals[p.platform];
      t.spend += spend; t.impressions += impressions; t.clicks += clicks;
      t.conversions += conversions; t.revenue += revenue; t.videoViews += videoViews; t.engagements += engagements;
    }
    day.spend = +day.spend.toFixed(2);
    day.revenue = +day.revenue.toFixed(2);
    day.conversions = +day.conversions.toFixed(1);
    day.ctr = day.impressions > 0 ? +((day.clicks / day.impressions) * 100).toFixed(2) : 0;
    days.push(day);
  }

  const totals = Object.values(pTotals).reduce(
    (acc, p) => {
      acc.spend += p.spend; acc.impressions += p.impressions; acc.clicks += p.clicks;
      acc.conversions += p.conversions; acc.revenue += p.revenue;
      acc.videoViews += p.videoViews; acc.engagements += p.engagements;
      return acc;
    },
    { spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0, videoViews: 0, engagements: 0 }
  );
  Object.assign(totals, KPI_FORMULAS(totals));
  totals.spend = +totals.spend.toFixed(2);
  totals.revenue = +totals.revenue.toFixed(2);
  totals.conversions = +totals.conversions.toFixed(1);
  totals.currency = currency;
  totals.excludedPlatforms = [];

  const byPlatform = Object.values(pTotals).map((p) => ({
    ...p,
    ...KPI_FORMULAS(p),
    spend: +p.spend.toFixed(2),
    revenue: +p.revenue.toFixed(2),
    conversions: +p.conversions.toFixed(1),
    currency,
  })).sort((a, b) => b.spend - a.spend);

  return { start, end, currency, totals, byPlatform, days, demo: true };
};

const ChartTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-lg px-3.5 py-2.5 text-xs">
      <p className="font-bold text-[#221C51] mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="flex items-center gap-1.5" style={{ color: entry.color || entry.stroke }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: entry.color || entry.stroke }} />
          {entry.name} : <span className="font-semibold">
            {['Dépense', 'Revenu', 'CPC', 'CPM'].includes(entry.name)
              ? fmt.currency(entry.value, currency)
              : entry.name === 'CTR' ? fmt.pct(entry.value) : fmt.number(entry.value)}
          </span>
        </p>
      ))}
    </div>
  );
};

// Camembert réutilisable (dépense / conversions par plateforme).
const PlatformDonut = ({ title, data, valueFormatter, emptyLabel }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
    <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide mb-2">{title}</h4>
    <ResponsiveContainer width="100%" height={230}>
      <PieChart>
        {data.length === 0 ? (
          <Pie data={[{ name: emptyLabel, value: 1 }]} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="78%" strokeWidth={0} isAnimationActive={false}>
            <Cell fill="#e5e7eb" />
          </Pie>
        ) : (
          <Pie data={data} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="78%" paddingAngle={3} strokeWidth={0}>
            {data.map((entry) => <Cell key={entry.name} fill={entry.hex} />)}
          </Pie>
        )}
        {data.length > 0 && <Tooltip formatter={valueFormatter} contentStyle={{ borderRadius: 12, border: '1px solid #f1f5f9', fontSize: 12 }} />}
        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
      </PieChart>
    </ResponsiveContainer>
  </div>
);

// Étape du funnel — pleine largeur sur mobile (empilé), proportionnelle sur desktop.
const FunnelStep = ({ label, value, pctOfPrev, pctLabel, color, width }) => (
  <div className="flex-1 min-w-0 w-full">
    <div className={`rounded-xl ${color} text-white px-3 py-2.5 md:px-4 md:py-3 transition-all duration-700 w-full md:w-auto`} style={{ maxWidth: '100%', width: `${width}%`, minWidth: '70%' }}>
      <p className="text-[9px] md:text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-base md:text-xl font-black leading-tight">{fmt.number(value)}</p>
    </div>
    {pctOfPrev != null && (
      <p className="text-[10px] md:text-xs text-gray-400 mt-1">{pctLabel} : <span className="font-semibold text-[#221C51]">{fmt.pct(pctOfPrev)}</span></p>
    )}
  </div>
);

const Statistics = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [presetKey, setPresetKey] = useState('30d');
  const [customStart, setCustomStart] = useState(fmt.isoDaysAgo(29));
  const [customEnd, setCustomEnd] = useState(todayIso());
  const [currency, setCurrency] = useState('USD');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const range = useMemo(() => {
    if (presetKey === 'custom') return { start: customStart, end: customEnd };
    const preset = PERIOD_PRESETS.find((p) => p.key === presetKey) || PERIOD_PRESETS[4];
    return preset.range();
  }, [presetKey, customStart, customEnd]);

  useEffect(() => {
    getRequest('/api/statistics')
      .then(setOverview)
      .catch(() => {})
      .finally(() => setLoadingOverview(false));
  }, []);

  useEffect(() => {
    getRequest('/api/auth/user-info')
      .then((u) => { if (u?.reporting_currency) setCurrency(u.reporting_currency); })
      .catch(() => {});
  }, []);

  const loadSummary = (r) => {
    setLoadingSummary(true);
    return getRequest(`/api/statistics/summary?start=${r.start}&end=${r.end}`)
      .then(setSummary)
      .catch(() => toast.error('Impossible de charger les statistiques.'))
      .finally(() => setLoadingSummary(false));
  };

  useEffect(() => {
    if (presetKey === 'custom' && (!customStart || !customEnd || customStart > customEnd)) return;
    loadSummary(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  const handleCurrencyChange = async (newCurrency) => {
    if (newCurrency === currency || savingCurrency) return;
    setSavingCurrency(true);
    try {
      await patchRequest('/api/auth/profile', { reportingCurrency: newCurrency });
      setCurrency(newCurrency);
      await loadSummary(range);
    } catch {
      toast.error("Impossible de changer la devise d'affichage.");
    } finally {
      setSavingCurrency(false);
    }
  };

  // Mode démo : aucune diffusion réelle sur la période chargée.
  const realTotals = summary?.totals || {};
  const hasRealData = (realTotals.spend || 0) > 0 || (realTotals.impressions || 0) > 0 || (realTotals.clicks || 0) > 0;
  const demoSummary = useMemo(
    () => buildDemoSummary(range.start, range.end, summary?.currency || currency),
    [range.start, range.end, summary?.currency, currency]
  );
  const demoMode = !loadingSummary && summary != null && !hasRealData;
  const effective = demoMode ? demoSummary : summary;

  if (loadingOverview && loadingSummary && !overview && !summary) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
        Chargement des statistiques…
      </div>
    );
  }

  const ov  = overview?.overview   || {};
  const rs  = overview?.ruleStats  || {};
  const spendTrend = overview?.spendTrend || [];
  const logTrend   = overview?.logTrend   || [];
  const platformBreakdown = overview?.platformBreakdown || [];

  const totals = effective?.totals || {};
  const byPlatform = effective?.byPlatform || [];
  const cur = effective?.currency || currency;

  const successPct = rs.total > 0 ? Math.round((rs.successCount / rs.total) * 100) : 0;
  const maxPlatform = Math.max(...platformBreakdown.map((p) => p.count), 1);
  const maxPlatformSpend = Math.max(...byPlatform.map((p) => p.spend || 0), 1);

  const days = (effective?.days || []).map((d) => ({ ...d, label: fmt.shortDate(d.date) }));
  const multiDay = days.length > 1;

  const donutSpend = byPlatform.filter((p) => (p.spend || 0) > 0).map((p) => ({
    name: PLATFORM_META[p.platform]?.label || p.platform,
    value: p.spend,
    hex: PLATFORM_META[p.platform]?.hex || '#94a3b8',
  }));
  const donutConv = byPlatform.filter((p) => (p.conversions || 0) > 0).map((p) => ({
    name: PLATFORM_META[p.platform]?.label || p.platform,
    value: p.conversions,
    hex: PLATFORM_META[p.platform]?.hex || '#94a3b8',
  }));

  const insights = [];
  if (totals.spend > 0) {
    const bestRoas = [...byPlatform].filter((p) => p.roas > 0).sort((a, b) => b.roas - a.roas)[0];
    if (bestRoas) insights.push(`🏆 Meilleur ROAS : ${PLATFORM_META[bestRoas.platform]?.label || bestRoas.platform} (${bestRoas.roas.toFixed(2)}× — ${fmt.currency(bestRoas.revenue, cur)} de revenu pour ${fmt.currency(bestRoas.spend, cur)})`);
    const bestDay = [...days].sort((a, b) => b.spend - a.spend)[0];
    if (bestDay && bestDay.spend > 0 && multiDay) insights.push(`📅 Pic de dépense le ${fmt.shortDate(bestDay.date)} : ${fmt.currency(bestDay.spend, cur)} pour ${fmt.number(bestDay.clicks)} clics`);
    const cheapest = [...byPlatform].filter((p) => p.cpc > 0).sort((a, b) => a.cpc - b.cpc)[0];
    if (cheapest && byPlatform.length > 1) insights.push(`🎯 CPC le plus bas : ${PLATFORM_META[cheapest.platform]?.label || cheapest.platform} à ${fmt.currency(cheapest.cpc, cur)}`);
    if (totals.roas > 0 && totals.roas < 1) insights.push(`⚠️ ROAS global < 1 (${totals.roas.toFixed(2)}×) — la période est déficitaire, demandez un diagnostic à Alex dans l'AI Launcher`);
  }

  const presetLabel = presetKey === 'custom'
    ? `${fmt.shortDate(range.start)} → ${fmt.shortDate(range.end)}`
    : PERIOD_PRESETS.find((p) => p.key === presetKey)?.label;

  return (
    <div className="p-3 md:p-6 max-w-5xl mx-auto space-y-5 md:space-y-8">

      {/* ── Header : devise + périodes ── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">Statistiques</h2>
          <select
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            disabled={savingCurrency}
            title="Devise d'affichage des totaux"
            className="text-xs font-semibold bg-gray-100 rounded-xl px-3 py-2 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20 disabled:opacity-50"
          >
            {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Sélecteur de période — scrollable horizontalement sur mobile */}
        <div className="flex flex-col gap-2" data-tour="stats-periods">
          <div className="flex items-center bg-gray-100 rounded-xl p-1 text-xs font-semibold gap-0.5 overflow-x-auto max-w-full -mx-1 px-1">
            {PERIOD_PRESETS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setPresetKey(opt.key)}
                className={`px-2.5 md:px-3.5 py-1.5 rounded-lg whitespace-nowrap transition-all flex-shrink-0 ${
                  presetKey === opt.key ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'
                }`}
              >
                {opt.label}
              </button>
            ))}
            <button
              onClick={() => setPresetKey('custom')}
              className={`px-2.5 md:px-3.5 py-1.5 rounded-lg whitespace-nowrap transition-all flex-shrink-0 ${
                presetKey === 'custom' ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'
              }`}
            >
              📅 Personnalisé
            </button>
          </div>

          {presetKey === 'custom' && (
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <input
                type="date"
                value={customStart}
                min={fmt.isoDaysAgo(92)}
                max={customEnd}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-gray-100 rounded-xl px-3 py-2 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20 font-semibold flex-1 min-w-[140px]"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={todayIso()}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-gray-100 rounded-xl px-3 py-2 border-none focus:outline-none focus:ring-2 focus:ring-[#221C51]/20 font-semibold flex-1 min-w-[140px]"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Getting Started banner ── */}
      {overview && ov.connectedPlatforms === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-[#221C51]/20 bg-gradient-to-r from-indigo-50 to-purple-50 p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-5">
          <div className="text-3xl md:text-4xl">🚀</div>
          <div className="flex-1">
            <h3 className="font-bold text-[#221C51] text-sm md:text-base mb-1">Commencez en connectant une plateforme</h3>
            <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
              Reliez Meta, TikTok, Google, Snapchat ou LinkedIn pour créer vos premières campagnes et voir vos statistiques réelles ici.
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-2 flex-shrink-0 w-full md:w-auto">
            <button
              onClick={() => navigate('/dashboard/connected-platforms')}
              className="px-5 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2d2570] transition-colors shadow-sm"
            >
              Connecter une plateforme →
            </button>
            <button
              onClick={() => navigate('/dashboard/create-ad-ai')}
              className="px-5 py-2.5 bg-white text-[#221C51] text-sm font-semibold rounded-xl border border-[#221C51]/20 hover:bg-gray-50 transition-colors"
            >
              Créer avec l'IA
            </button>
          </div>
        </div>
      )}

      {/* ── Performance media buying ── */}
      <section>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 mb-3">
          <h3 className="text-xs md:text-sm font-bold text-[#221C51] uppercase tracking-wide">
            Performance publicitaire — {presetLabel}
          </h3>
          {loadingSummary ? (
            <span className="text-xs text-gray-400 animate-pulse">Actualisation…</span>
          ) : (
            <span className="text-[10px] md:text-xs text-gray-400">
              Toutes campagnes (délivraison réelle) · rafraîchi toutes les 15 min
            </span>
          )}
        </div>

        {/* Badge mode démo */}
        {demoMode && (
          <div className="mb-4 rounded-xl bg-purple-50 border border-purple-200 px-4 py-2.5 text-xs text-purple-700 flex items-center gap-2">
            <span>🧪</span>
            <span>
              <strong>Données de démonstration</strong> — aucune diffusion réelle détectée sur cette période.
              Ces chiffres illustrent le dashboard ; vos vraies statistiques les remplaceront automatiquement dès la première diffusion.
            </span>
          </div>
        )}

        {totals.excludedPlatforms?.length > 0 && (
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-700 flex items-center gap-2">
            <span>⚠️</span>
            <span>
              Devise indéterminée pour {totals.excludedPlatforms.map((p) => PLATFORM_META[p]?.label || p).join(', ')} —
              montants exclus des totaux convertis.
            </span>
          </div>
        )}

        {/* 1 ── CAMEMBERTS d'abord */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5" data-tour="stats-charts">
          <PlatformDonut
            title="🥧 Dépense par plateforme"
            data={donutSpend}
            valueFormatter={(v) => fmt.currency(v, cur)}
            emptyLabel="En attente de dépenses"
          />
          <PlatformDonut
            title="🥧 Conversions par plateforme"
            data={donutConv}
            valueFormatter={(v) => fmt.number(v)}
            emptyLabel="En attente de conversions"
          />
        </div>

        {/* 2 ── KPIs : Résultats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-3 md:mb-4" data-tour="stats-kpis">
          <KpiCard label="Dépenses"    value={fmt.currency(totals.spend, cur)}   color="bg-[#221C51] text-white"       icon="💸" />
          <KpiCard label="Revenu"      value={totals.revenue > 0 ? fmt.currency(totals.revenue, cur) : '—'} sub="attribué aux pubs" color="bg-emerald-50 text-emerald-900" icon="💰" />
          <KpiCard label="ROAS"        value={totals.roas > 0 ? `${Number(totals.roas).toFixed(2)}×` : '—'} sub="revenu / dépense" color="bg-teal-50 text-teal-900" icon="📊" />
          <KpiCard label="Conversions" value={fmt.number(totals.conversions)}    sub={totals.conversionRate > 0 ? `taux ${fmt.pct(totals.conversionRate)}` : 'achats / leads'} color="bg-green-50 text-green-800" icon="🛒" />
          <KpiCard label="CPA"         value={totals.cpa > 0 ? fmt.currency(totals.cpa, cur) : '—'} sub="coût / acquisition" color="bg-orange-50 text-orange-900" icon="🎯" />
        </div>

        {/* 3 ── KPIs : Diffusion (CTR…) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-3 md:mb-4">
          <KpiCard label="Impressions" value={fmt.number(totals.impressions)}    color="bg-indigo-50 text-[#221C51]"   icon="👁" />
          <KpiCard label="Clics"       value={fmt.number(totals.clicks)}         color="bg-blue-50 text-blue-900"      icon="🖱" />
          <KpiCard label="CTR"         value={fmt.pct(totals.ctr)}               sub="clics / impressions" color="bg-purple-50 text-purple-900" icon="📈" />
          <KpiCard label="CPC"         value={fmt.currency(totals.cpc, cur)}     sub="coût / clic" color="bg-yellow-50 text-yellow-800" icon="👆" />
          <KpiCard label="CPM"         value={fmt.currency(totals.cpm, cur)}     sub="coût / 1000 impr." color="bg-rose-50 text-rose-900" icon="📡" />
        </div>

        {/* 4 ── KPIs : Vidéo & engagement */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-5 md:mb-6">
          <KpiCard label="Vues vidéo"      value={fmt.number(totals.videoViews)}   color="bg-fuchsia-50 text-fuchsia-900" icon="▶️" />
          <KpiCard label="Taux de vue"     value={fmt.pct(totals.viewRate)}        sub="vues / impressions" color="bg-violet-50 text-violet-900" icon="🎬" />
          <KpiCard label="CPV"             value={totals.cpv > 0 ? fmt.currency(totals.cpv, cur) : '—'} sub="coût / vue vidéo" color="bg-cyan-50 text-cyan-900" icon="💵" />
          <KpiCard label="Engagements"     value={fmt.number(totals.engagements)}  sub="likes, partages…" color="bg-lime-50 text-lime-900" icon="❤️" />
          <KpiCard label="Taux d'engag."   value={fmt.pct(totals.engagementRate)}  sub={totals.costPerEngagement > 0 ? `coût/engag. ${fmt.currency(totals.costPerEngagement, cur)}` : 'engag. / impressions'} color="bg-amber-50 text-amber-900" icon="🤝" />
        </div>

        {/* 5 ── Funnel (empilé sur mobile) */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5 mb-5 md:mb-6">
          <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide mb-4">Funnel de conversion</h4>
          <div className="flex flex-col md:flex-row items-stretch md:items-start gap-3 md:gap-5">
            <FunnelStep label="Impressions" value={totals.impressions} color="bg-[#221C51]" width={100} />
            <FunnelStep label="Clics" value={totals.clicks} color="bg-blue-500" width={84} pctOfPrev={totals.ctr} pctLabel="CTR" />
            <FunnelStep label="Conversions" value={totals.conversions} color="bg-emerald-500" width={68} pctOfPrev={totals.conversionRate} pctLabel="Taux de conv." />
          </div>
        </div>

        {/* 6 ── Courbe dépense & revenu */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide">Dépense & revenu par jour</h4>
            <span className="text-[10px] text-gray-400">{cur}</span>
          </div>
          {!multiDay ? (
            <div className="py-8 text-center">
              <p className="text-2xl md:text-3xl font-black text-[#221C51]">{fmt.currency(totals.spend, cur)}</p>
              <p className="text-xs text-gray-400 mt-1">dépensés sur la journée · {fmt.number(totals.clicks)} clics · {fmt.number(totals.conversions)} conversions</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <ComposedChart data={days} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={BRAND} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt.compact} width={40} />
                <Tooltip content={<ChartTooltip currency={cur} />} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                <Area type="monotone" dataKey="spend" name="Dépense" stroke={BRAND} strokeWidth={2.5} fill="url(#spendGrad)" />
                <Line type="monotone" dataKey="revenue" name="Revenu" stroke="#10b981" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* 7 ── Clics, conversions & CTR */}
        {multiDay && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5 mb-5 md:mb-6">
            <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide mb-3">Clics, conversions & CTR par jour</h4>
            <ResponsiveContainer width="100%" height={210}>
              <ComposedChart data={days} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt.compact} width={40} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} width={36} />
                <Tooltip content={<ChartTooltip currency={cur} />} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                <Bar yAxisId="left" dataKey="clicks" name="Clics" fill="#60a5fa" radius={[4, 4, 0, 0]} maxBarSize={16} />
                <Bar yAxisId="left" dataKey="conversions" name="Conversions" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={16} />
                <Line yAxisId="right" type="monotone" dataKey="ctr" name="CTR" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 8 ── Insights */}
        {insights.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100 p-4 md:p-5 mb-5 md:mb-6">
            <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide mb-2.5">💡 À retenir sur la période</h4>
            <ul className="space-y-1.5">
              {insights.map((line) => (
                <li key={line} className="text-xs md:text-sm text-gray-600 leading-relaxed">{line}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 9 ── Détail par plateforme */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 md:px-5 py-3.5 md:py-4 border-b border-gray-50">
            <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide">Détail par plateforme</h4>
          </div>
          <div className="divide-y divide-gray-50">
            {byPlatform.map((p) => {
              const meta = PLATFORM_META[p.platform] || { label: p.platform, color: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700' };
              return (
                <div key={p.platform} className="px-3 md:px-5 py-3 md:py-4 hover:bg-gray-50 transition">
                  <div className="flex items-center gap-2 md:gap-4 mb-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.badge}`}>{meta.label}</span>
                    <span className="ml-auto text-right">
                      <span className="block text-sm font-bold text-[#221C51]">{fmt.currency(p.spend, cur)}</span>
                      {p.nativeCurrency && p.nativeCurrency !== cur && (
                        <span className="block text-[10px] text-gray-400">converti depuis {fmt.currency(p.nativeSpend, p.nativeCurrency)}</span>
                      )}
                    </span>
                  </div>
                  <Bar2 value={p.spend} max={maxPlatformSpend} color={meta.color} />
                  <div className="flex flex-wrap gap-x-3 md:gap-x-6 gap-y-1 mt-2 text-[11px] md:text-xs text-gray-500">
                    <span><span className="font-medium text-[#221C51]">{fmt.number(p.impressions)}</span> impr.</span>
                    <span><span className="font-medium text-[#221C51]">{fmt.number(p.clicks)}</span> clics</span>
                    <span>CTR <span className="font-medium text-[#221C51]">{fmt.pct(p.ctr)}</span></span>
                    <span>CPC <span className="font-medium text-[#221C51]">{fmt.currency(p.cpc, cur)}</span></span>
                    <span>CPM <span className="font-medium text-[#221C51]">{fmt.currency(p.cpm, cur)}</span></span>
                    {(p.conversions || 0) > 0 && <span>Conv. <span className="font-medium text-[#221C51]">{fmt.number(p.conversions)}</span></span>}
                    {(p.cpa || 0) > 0 && <span>CPA <span className="font-medium text-[#221C51]">{fmt.currency(p.cpa, cur)}</span></span>}
                    {(p.roas || 0) > 0 && <span>ROAS <span className="font-medium text-emerald-600">{p.roas.toFixed(2)}×</span></span>}
                    {(p.videoViews || 0) > 0 && <span>Vues <span className="font-medium text-[#221C51]">{fmt.number(p.videoViews)}</span></span>}
                    {(p.engagements || 0) > 0 && <span>Engag. <span className="font-medium text-[#221C51]">{fmt.number(p.engagements)}</span></span>}
                  </div>
                </div>
              );
            })}
            {byPlatform.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">Aucune plateforme avec diffusion sur la période.</p>
            )}
          </div>
        </div>
      </section>

      {/* ── Vue d'ensemble ── */}
      {overview && (
        <section>
          <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-3">Vue d'ensemble</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <KpiCard label="Plateformes connectées" value={ov.connectedPlatforms}           color="bg-[#221C51] text-white"      icon="🔗" />
            <KpiCard label="Règles actives"          value={`${ov.activeRules || 0} / ${ov.totalRules || 0}`} sub="règles configurées" color="bg-indigo-50 text-[#221C51]" icon="⚡" />
            <KpiCard label="Exécutions de règles"    value={ov.totalExecutions || 0}        sub={`${successPct}% de succès`}     color="bg-green-50 text-green-800"   icon="🤖" />
            <KpiCard label="Wallet"                  value={`€${ov.walletBalance || '0.00'}`} sub={ov.isPremium ? 'Plan Premium ✓' : 'Plan Free'} color="bg-yellow-50 text-yellow-800" icon="💼" />
          </div>
        </section>
      )}

      {/* ── Règles ── */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
            <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-4">Performance des règles</h3>
            {!rs.total ? (
              <p className="text-xs text-gray-400 text-center py-6">Aucune exécution de règle enregistrée.</p>
            ) : (
              <div className="space-y-4">
                {[
                  { label: 'Succès',  count: rs.successCount, pct: successPct, color: 'bg-green-500', text: 'text-green-600' },
                  { label: 'Échoué', count: rs.failedCount,   pct: rs.total > 0 ? Math.round((rs.failedCount  / rs.total) * 100) : 0, color: 'bg-red-400',  text: 'text-red-500'  },
                  { label: 'Ignoré', count: rs.skippedCount,  pct: rs.total > 0 ? Math.round((rs.skippedCount / rs.total) * 100) : 0, color: 'bg-gray-400', text: 'text-gray-500' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={`font-semibold ${row.text}`}>{row.label}</span>
                      <span className="text-gray-400">{row.count} ({row.pct}%)</span>
                    </div>
                    <Bar2 value={row.count} max={rs.total} color={row.color} />
                  </div>
                ))}
                <p className="text-xs text-gray-400 text-right pt-1">Total : {rs.total} exécutions</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
            <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-4">Activité règles par plateforme</h3>
            {platformBreakdown.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">Aucune activité enregistrée.</p>
            ) : (
              <div className="space-y-3">
                {platformBreakdown.sort((a, b) => b.count - a.count).map((p) => (
                  <div key={p.platform}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-[#221C51] capitalize">{PLATFORM_META[p.platform]?.label || p.platform}</span>
                      <span className="text-gray-400">{p.count}</span>
                    </div>
                    <Bar2 value={p.count} max={maxPlatform} color={PLATFORM_META[p.platform]?.color || 'bg-gray-400'} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Recharges wallet + exécutions — 6 derniers mois ── */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
            <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-4">
              Recharges wallet — 6 derniers mois
            </h3>
            {spendTrend.every((s) => s.amount === 0) ? (
              <p className="text-xs text-gray-400 text-center py-10">Aucun paiement sur les 6 derniers mois.</p>
            ) : (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={spendTrend} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt.compact} width={38} />
                  <Tooltip formatter={(v) => `€${Number(v).toFixed(2)}`} contentStyle={{ borderRadius: 12, border: '1px solid #f1f5f9', fontSize: 12 }} />
                  <Bar dataKey="amount" name="Recharge" fill={BRAND} radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
            <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-4">
              Exécutions de règles — 6 derniers mois
            </h3>
            {logTrend.every((l) => l.total === 0) ? (
              <p className="text-xs text-gray-400 text-center py-10">Aucune exécution sur les 6 derniers mois.</p>
            ) : (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={logTrend} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} width={38} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #f1f5f9', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="success" name="Succès" stackId="logs" fill="#22c55e" maxBarSize={36} />
                  <Bar dataKey="failed"  name="Échoué" stackId="logs" fill="#f87171" maxBarSize={36} />
                  <Bar dataKey="skipped" name="Ignoré" stackId="logs" fill="#d1d5db" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
